import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { apolloClient } from '../services/apollo-client';
import '../i18n/config';
import { AppLayout } from './AppLayout';
import { AppErrorBoundary } from './AppErrorBoundary';
import { AppUIProvider } from './AppUIProvider';
import { AuthProvider } from '@/features/auth';
import { FeatureFlagProvider } from '@/features/feature-flags';
import { tappeerTheme } from '@/features/ai-chat/tappeer-theme';
import { JuneauProvider, juneauCs, juneauEn } from 'juneau';
import 'juneau/dist/style.css';

/** Juneau provider with Tappeer theme and labels following the active UI language. */
function AppJuneauProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;

  const labels = useMemo(() => {
    const base = language === 'cs' ? juneauCs : juneauEn;
    return {
      ...base,
      sidebarTitle: t('features.aiChat.title'),
      connectingMessage: t('features.aiChat.connectingMessage'),
      inputPlaceholder: t('features.aiChat.inputPlaceholder'),
      typingMessage: t('features.aiChat.typingMessage'),
      userLabel: t('features.aiChat.userLabel')
    };
  }, [language, t]);

  return (
    <JuneauProvider theme={tappeerTheme} labels={labels} defaultTitle={t('features.aiChat.title')}>
      {children}
    </JuneauProvider>
  );
}

function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <BrowserRouter>
        <AppErrorBoundary>
          <AuthProvider>
            <FeatureFlagProvider>
              <AppUIProvider>
                <AppJuneauProvider>
                  <AppLayout />
                </AppJuneauProvider>
              </AppUIProvider>
            </FeatureFlagProvider>
          </AuthProvider>
        </AppErrorBoundary>
      </BrowserRouter>
    </ApolloProvider>
  );
}

export default App;
